/**
 * useTrainingQuizLauncher — shared, flag-gated launcher for the post-completion
 * knowledge-check quiz (Phase Q1). Used by every surface that can offer a quiz
 * after a technician marks training complete: the mobile self-attestation prompt,
 * the desktop/Companion completion prompt, and Passport History.
 *
 * It centralizes three concerns so no caller reimplements them:
 *   1. The `trainingQuizMvp` feature gate (OFF → launch() is a no-op).
 *   2. An approved-quiz PRE-CHECK — the modal only opens when an approved quiz
 *      actually exists for that artifact (no empty modal after completion), and
 *      the fetched quiz is handed to the modal so it doesn't re-fetch.
 *   3. Refreshing the history/return-prompt queries after an attempt so the
 *      quiz badge/button update without a manual reload.
 *
 * Integrity: this never enables the flag, never grades locally, and never claims
 * watch/Companion/provider verification — passing is a knowledge signal only.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { createSupabaseQuizAdapter, type QuizAdapter } from '@/lib/training/quiz/quizAdapter';
import type { QuizForTechnician } from '@/lib/training/quiz/quizTypes';

export interface QuizLaunchTarget {
  artifactId: string;
  sessionId: string | null;
  /** Pre-fetched technician quiz (answers already stripped server-side). */
  quiz: QuizForTechnician;
}

export interface TrainingQuizLauncher {
  quizEnabled: boolean;
  adapter: QuizAdapter;
  target: QuizLaunchTarget | null;
  /** Opens the quiz ONLY if the flag is on AND an approved quiz exists. */
  launch: (artifactId: string | null | undefined, sessionId: string | null) => Promise<boolean>;
  close: () => void;
  /** Pass as the modal's onResult — refreshes history/return-prompt views. */
  handleResult: () => void;
}

export function useTrainingQuizLauncher(): TrainingQuizLauncher {
  const quizEnabled = useFeatureFlag('trainingQuizMvp');
  const adapter = useMemo(() => createSupabaseQuizAdapter(), []);
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<QuizLaunchTarget | null>(null);

  const launch = useCallback(
    async (artifactId: string | null | undefined, sessionId: string | null): Promise<boolean> => {
      if (!quizEnabled || !artifactId) return false;
      // Pre-check: only open when an APPROVED quiz exists for this artifact.
      const quiz = await adapter.getQuizForTechnician(artifactId);
      if (!quiz) return false;
      setTarget({ artifactId, sessionId, quiz });
      return true;
    },
    [quizEnabled, adapter],
  );

  const close = useCallback(() => setTarget(null), []);

  const handleResult = useCallback(() => {
    // A recorded attempt updates session metadata.trainingQuiz → refresh the
    // views that render the quiz badge / "Take quiz" button.
    void queryClient.invalidateQueries({ queryKey: ['training-session-history'] });
    void queryClient.invalidateQueries({ queryKey: ['training-completion-return-prompt'] });
  }, [queryClient]);

  return { quizEnabled, adapter, target, launch, close, handleResult };
}
